import {useCallback, useEffect, useMemo, useState} from 'react';

import appStore from 'client/AppStore';
import {callAction} from 'client/framework';
import type {DropdownNavItem, MainNavItem, NavItems, NavItemZone} from './types';
import {getTeacherHasClassSubjects} from './GlobalNav.queries';
import {hasLicenseAccess as getHasLicenseAccess} from 'lib/UserAccessUtil';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import sessionStore from 'client/Session/SessionStore';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import usePathname from 'lib/hooks/usePathname';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

export default function useNavItems() {
  const {isLoading: isLoadingFeatureFlagStatus, isFeatureEnabled: isNewStudentDashboardEnabled} =
    useIsFeatureFlagEnabled('new_student_dashboard');
  const hasClassrooms = useStudentDashboard((state) => state.classrooms.size > 0);

  const pathname = usePathname();

  const getHomeRoute = useCallback(() => {
    if (!pathname.startsWith('/home')) {
      return '/home';
    }

    return hasClassrooms ? '/home/welcome' : '/home/assignments';
  }, [hasClassrooms, pathname]);

  const isSignUpPage = window.location.pathname.includes('/signup');
  const linkZone = appStore.isZoneDennis() ? 'dennis' : 'albert';

  // Permissions information
  const hasAuthorAccess = sessionStore.hasAuthorAccess();
  const hasContentManagerAccess = sessionStore.hasContentManagerAccess();
  const hasTeacherAccess = sessionStore.hasTeacherAccess();
  const hasValidSession = sessionStore.hasValidSession();
  const isAuthor = sessionStore.isAuthor();
  const isSchoolAdministrator = sessionStore.isSchoolAdministrator();
  const isStudent = sessionStore.isStudent();
  const isSuper = sessionStore.isSuper();
  const isTeacher = sessionStore.isTeacher();

  const [isTeacherWithClassSubjects, setIsTeacherWithClassSubjects] = useState(false);

  const {isReady: isHasLicenseAccessReady, value: hasLicenseAccess} = getHasLicenseAccess();

  const NAV_ITEMS = useMemo<NavItems>(() => {
    const logOut: DropdownNavItem = {
      action: () => callAction(sessionLifecycleActions.END_SESSION),
      icon: ['fal', 'sign-out'],
      key: 'logout',
      name: 'Log Out'
    };

    return {
      albert: {
        dropdownNavItems: [
          {
            icon: ['fal', 'user'],
            key: 'account',
            name: 'My Account',
            showSeparator: true,
            route: '/account/profile'
          },
          {
            icon: ['fal', 'chart-line'],
            key: 'my-data',
            name: 'My Data',
            showSeparator: true,
            route: '/my-data'
          },
          {
            hasPermission: isAuthor && !hasContentManagerAccess,
            icon: ['fal', 'pen-nib'],
            key: 'authoring',
            name: 'Authoring',
            route: '/dennis/content/dashboard'
          },
          {
            hasPermission: hasContentManagerAccess,
            icon: ['fal', 'wrench'],
            key: 'dennis',
            name: 'Dennis',
            route: '/dennis'
          },
          {
            hardLink: true,
            hasPermission: !isStudent,
            icon: ['fal', 'book'],
            key: 'resource-hub',
            name: 'Resource Hub',
            route: '/resource-hub/'
          },
          {
            hardLink: true,
            icon: ['fal', 'question-circle'],
            key: 'help',
            name: 'Help',
            route: 'https://help.albert.io/en/'
          },
          {
            hasPermission: isSuper || isTeacher,
            icon: ['fal', 'comment-pen'],
            key: 'feedback',
            name: 'Rate Albert'
          },
          logOut
        ],
        mainNavItems: [
          {
            hasPermission:
              isStudent ||
              (isAuthor && !hasTeacherAccess) ||
              (isTeacher && !isTeacherWithClassSubjects),
            key: 'library',
            matchingRoutes: ['/subjects'],
            name: 'Library',
            route: '/subjects'
          },
          {
            hasPermission: isTeacher && isTeacherWithClassSubjects,
            key: 'library-class-subjects',
            matchingRoutes: ['/subjects'],
            name: 'Library',
            route: '/subjects/class-subjects'
          },
          {
            hasPermission:
              !isStudent || (!isLoadingFeatureFlagStatus && !isNewStudentDashboardEnabled),
            key: 'classes',
            name: 'Classes',
            route: '/classes'
          },
          {
            hasPermission: !isLoadingFeatureFlagStatus && isStudent && isNewStudentDashboardEnabled,
            icon: ['far', 'home'],
            key: 'home',
            name: 'Home',
            route: getHomeRoute(),
            routesArr: ['/home']
          },
          {
            hasPermission: hasTeacherAccess,
            key: 'reports',
            name: 'Reports',
            route: '/reports'
          },
          {
            hasPermission: hasTeacherAccess,
            key: 'assignments',
            name: 'Assignments',
            route: '/assignments',
            routesArr: ['/saved', '/assignments', '/assignment-builder/create-assignment']
          },
          {
            hasPermission: isSchoolAdministrator || isSuper,
            key: 'admin',
            name: 'Admin',
            route: '/school-admin'
          },
          {
            hardLink: true,
            hasPermission: (isHasLicenseAccessReady && !hasLicenseAccess) || isSuper,
            key: 'pricing',
            name: 'Pricing',
            route: '/pricing-schools'
          }
        ]
      },
      dennis: {
        dropdownNavItems: [
          {
            hardLink: true,
            key: 'backToAlbert',
            name: 'Back to Albert',
            route: '/subjects'
          },
          logOut
        ],
        mainNavItems: [
          {
            hasPermission: hasAuthorAccess,
            key: 'content',
            name: 'Content',
            route: '/dennis/content'
          },
          {
            hasPermission: isSuper,
            key: 'users',
            name: 'Users',
            route: '/dennis/users'
          },
          {
            hasPermission: isSuper,
            key: 'districts',
            name: 'Districts',
            route: '/dennis/districts'
          },
          {
            hasPermission: isSuper,
            key: 'schools',
            name: 'Schools',
            route: '/dennis/schools'
          }
        ]
      }
    };
  }, [
    getHomeRoute,
    hasAuthorAccess,
    hasContentManagerAccess,
    hasLicenseAccess,
    hasTeacherAccess,
    isAuthor,
    isHasLicenseAccessReady,
    isLoadingFeatureFlagStatus,
    isNewStudentDashboardEnabled,
    isSchoolAdministrator,
    isStudent,
    isSuper,
    isTeacher,
    isTeacherWithClassSubjects
  ]);

  useEffect(() => {
    if (isTeacher) {
      (async () => {
        const teacherHasClassSubjects = await getTeacherHasClassSubjects();
        setIsTeacherWithClassSubjects(teacherHasClassSubjects);
      })();
    }
  }, [isTeacher, setIsTeacherWithClassSubjects]);

  const filteredNavItems: NavItemZone = useMemo(() => {
    if (!hasValidSession && isSignUpPage) {
      return {
        dropdownNavItems: [],
        mainNavItems: []
      };
    }

    const dropdownNavItems = NAV_ITEMS[linkZone].dropdownNavItems.filter(
      ({hasPermission = true}) => hasPermission
    );
    const mainNavItems = NAV_ITEMS[linkZone].mainNavItems.filter(
      ({hasPermission = true}) => hasPermission
    );

    return {
      dropdownNavItems,
      mainNavItems
    };
  }, [hasValidSession, isSignUpPage, linkZone, NAV_ITEMS]);

  function getIsMatchingRoute(navItem: MainNavItem) {
    const {pathname} = window.location;
    const {matchingRoutes = [], route: linkRoute, routesArr = []} = navItem;

    let ret = false;

    if (routesArr.length > 0) {
      ret = routesArr.some((route) => pathname.startsWith(route));
    } else if (
      /**
       * This ensures that the Subjects header link will contain the active class
       * regardless of which group/subject combination is rendered. It will be replaced soon.
       */
      matchingRoutes.includes(pathname) ||
      linkRoute === pathname ||
      pathname.startsWith(linkRoute) ||
      matchingRoutes.filter((path) => pathname.indexOf(path) === 0).length > 0
    ) {
      ret = true;
    }
    return ret;
  }

  return {
    dropdownNavItems: filteredNavItems.dropdownNavItems,
    getIsMatchingRoute,
    mainNavItems: filteredNavItems.mainNavItems
  };
}
